import {AppleIcon} from '@github-ui/custom-icons/AppleIcon'
import {LinuxIcon} from '@github-ui/custom-icons/LinuxIcon'
import {WindowsIcon} from '@github-ui/custom-icons/WindowsIcon'
import {getOS, OS, type OS as OperatingSystem} from '@github-ui/get-os'
import type {Icon} from '@primer/octicons-react'
import type {CSSProperties} from 'react'
import {useSyncExternalStore} from 'react'

import backgroundDark from './assets/background-dark.png'
import backgroundLight from './assets/background-light.png'
import bannerDark from './assets/banner-dark.png'
import bannerLight from './assets/banner-light.png'
import styles from './CopilotAppBanner.module.css'
import {
  PromotionalBanner,
  type PromotionalBannerAction,
  type PromotionalBannerPayload,
} from '../promotional-banner/PromotionalBanner'

export type CopilotAppDownloadPlatform = 'mac' | 'windows' | 'linux'

export interface CopilotAppBannerProps {
  banner: PromotionalBannerPayload
  rounded?: boolean
  downloadPlatform?: CopilotAppDownloadPlatform
}

const ICON_BY_PLATFORM: Record<CopilotAppDownloadPlatform, Icon> = {
  mac: AppleIcon,
  windows: WindowsIcon,
  linux: LinuxIcon,
}

const DOWNLOAD_LABEL_BY_PLATFORM: Record<CopilotAppDownloadPlatform, string> = {
  mac: 'Download for macOS',
  windows: 'Download for Windows',
  linux: 'Download for Linux',
}

// Keyed on the campaign's action ids rather than `variant`: `variant` is the Datadog tag, and
// renaming one for reporting should not silently change which download the CTA offers.
const PLATFORM_BY_ACTION_ID: Record<string, CopilotAppDownloadPlatform> = {
  'download-mac-silicon': 'mac',
  'download-mac-intel': 'mac',
  'download-win-x64': 'windows',
  'download-win-arm': 'windows',
  'download-linux': 'linux',
}

const DEFAULT_DOWNLOAD_PLATFORM: CopilotAppDownloadPlatform = 'mac'
const TELEMETRY = {component: 'CopilotAppBanner', variant: 'download'}
const ARTWORK_STYLE = {
  '--copilot-app-banner-light': `url(${bannerLight})`,
  '--copilot-app-banner-dark': `url(${bannerDark})`,
  '--copilot-app-banner-light-compact': `url(${backgroundLight})`,
  '--copilot-app-banner-dark-compact': `url(${backgroundDark})`,
} as CSSProperties

function subscribeToDownloadPlatform() {
  return () => {}
}

export function resolveDownloadPlatform(os: OperatingSystem): CopilotAppDownloadPlatform {
  switch (os) {
    case OS.Windows:
      return 'windows'
    case OS.Linux:
      return 'linux'
    case OS.macOS:
    default:
      return DEFAULT_DOWNLOAD_PLATFORM
  }
}

function getBrowserDownloadPlatform(): CopilotAppDownloadPlatform {
  return resolveDownloadPlatform(getOS().os)
}

function getServerDownloadPlatform(): CopilotAppDownloadPlatform {
  return DEFAULT_DOWNLOAD_PLATFORM
}

function useDetectedDownloadPlatform() {
  return useSyncExternalStore(subscribeToDownloadPlatform, getBrowserDownloadPlatform, getServerDownloadPlatform)
}

function platformOf(action: PromotionalBannerAction): CopilotAppDownloadPlatform | undefined {
  return PLATFORM_BY_ACTION_ID[action.id]
}

export function CopilotAppBanner({banner, rounded = true, downloadPlatform}: CopilotAppBannerProps) {
  const detectedDownloadPlatform = useDetectedDownloadPlatform()
  const resolvedPlatform = downloadPlatform ?? detectedDownloadPlatform
  const download = banner.actionMenu?.items.find(item => platformOf(item) === resolvedPlatform)
  // No variant here, so platform-specific download clicks keep the banner's `download` variant.
  const primaryAction = download
    ? {
        id: download.id,
        label: DOWNLOAD_LABEL_BY_PLATFORM[resolvedPlatform],
        href: download.href,
        analyticsAction: download.analyticsAction,
        openInNewTab: download.openInNewTab,
      }
    : banner.primaryAction

  return (
    <PromotionalBanner
      banner={{...banner, primaryAction}}
      rounded={rounded}
      surfaceClassName={styles.Surface}
      surfaceStyle={ARTWORK_STYLE}
      actionVisual={action => {
        const platform = platformOf(action)
        if (!platform) return null
        const PlatformIcon = ICON_BY_PLATFORM[platform]
        return <PlatformIcon />
      }}
      telemetry={TELEMETRY}
    />
  )
}
